import React, { useEffect, useRef, useState } from 'react'

function HeroSection() {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const [startAnimation, setStartAnimation] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return undefined
    }

    const immediateTimer = setTimeout(() => {
      setStartAnimation(true)
    }, 50)

    const handleCanPlay = () => {
      if (video.readyState >= 3) {
        video.play().catch((err) => console.log('Autoplay failed:', err))
      }
    }

    const handleVideoEnd = () => {
      video.pause()
      if (video.duration) {
        video.currentTime = video.duration - 0.001
      }
    }

    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('canplaythrough', handleCanPlay)
    video.addEventListener('ended', handleVideoEnd)

    video.load()

    if (video.readyState >= 3) {
      handleCanPlay()
    }

    return () => {
      clearTimeout(immediateTimer)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('canplaythrough', handleCanPlay)
      video.removeEventListener('ended', handleVideoEnd)
    }
  }, [])

  return (
    <section
      className='hero-container'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: 'calc(100vh - 28px)',
        minHeight: 'calc(100vh - 28px)',
        paddingTop: 'clamp(80px, 12vh, 130px)',
        paddingBottom: '0',
        paddingLeft: 'max(20px, calc((100% - 1200px) / 2))',
        paddingRight: 'max(20px, calc((100% - 1200px) / 2))',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <style>
        {`
          @keyframes breatheOut {
            0% {
              opacity: 0;
              transform: translateY(50px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media (max-width: 768px) {
            .hero-title {
              font-size: clamp(24px, 6vw, 36px) !important;
            }
            .hero-subtitle {
              font-size: 14px !important;
            }
            .hero-button {
              padding: 12px 32px !important;
              font-size: 14px !important;
            }
          }

          @media (min-width: 769px) and (max-width: 1024px) {
            .hero-title {
              font-size: clamp(32px, 4vw, 42px) !important;
            }
          }

          @media (min-width: 1280px) and (max-height: 850px) {
            .hero-container {
              padding-top: 80px !important;
              height: calc(100vh - 80px) !important;
              min-height: calc(100vh - 80px) !important;
            }
            .hero-title {
              font-size: 48px !important;
              margin-bottom: 12px !important;
            }
            .hero-subtitle {
              margin-bottom: 20px !important;
            }
            .hero-button {
              margin-bottom: 2vh !important;
            }
          }
        `}
      </style>

      <h1
        className='hero-title'
        style={{
          fontSize: 'clamp(40px, 4.5vw, 62px)',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: 'clamp(16px, 2.5vh, 24px)',
          marginTop: '0',
          lineHeight: '1.1',
          letterSpacing: '-0.02em',
          fontFamily: "'Univers', 'Roboto', sans-serif",
          background: 'linear-gradient(90deg, #D7DF23 0%, #D7DF23 43%, #8B1D8A 100%) 0% 0% no-repeat padding-box',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent',
          opacity: 0,
          transform: 'translateY(50px)',
          animationName: startAnimation ? 'breatheOut' : 'none',
          animationDuration: '3.3s',
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards',
          animationDelay: '0s',
          maxWidth: '90%',
          zIndex: 10,
          position: 'relative'
        }}
      >
        One Platform. Endless Possibilities.
      </h1>

      <p
        className='hero-subtitle'
        style={{
          fontSize: 'clamp(16px, 1.8vw, 22px)',
          letterSpacing: '0px',
          color: '#FFFFFF',
          textAlign: 'center',
          marginBottom: 'clamp(16px, 2.5vh, 24px)',
          marginTop: '0',
          opacity: 0,
          transform: 'translateY(50px)',
          animationName: startAnimation ? 'breatheOut' : 'none',
          animationDuration: '3.3s',
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards',
          animationDelay: '0.5s',
          lineHeight: '1.5',
          fontFamily: "'Univers', 'Roboto', sans-serif",
          fontWeight: '400',
          zIndex: 10,
          position: 'relative'
        }}
      >
        Keep every team aligned and make confident decisions with real-time project insights.
      </p>

      <button
        onClick={() => window.open('https://forms.office.com/e/hnzxMZbBSK', '_blank')}
        className='hero-button'
        style={{
          backgroundColor: '#0ea5e9',
          color: '#ffffff',
          border: 'none',
          borderRadius: '50px',
          padding: '14px 40px',
          fontSize: '16px',
          fontWeight: '700',
          fontFamily: "'Univers', 'Roboto', sans-serif",
          cursor: 'pointer',
          marginBottom: '2.5vh',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)',
          opacity: 0,
          transform: 'translateY(50px)',
          animationName: startAnimation ? 'breatheOut' : 'none',
          animationDuration: '3.3s',
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards',
          animationDelay: '1s',
          zIndex: 10,
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          if (!startAnimation) {
            return
          }
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.4)'
        }}
        onMouseLeave={(e) => {
          if (!startAnimation) {
            return
          }
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(14, 165, 233, 0.3)'
        }}
      >
        Book a Demo Today
      </button>

      <div
        ref={containerRef}
        style={{
          width: '100%',
          maxWidth: '95vw',
          flex: 1,
          minHeight: '0',
          position: 'relative',
          borderRadius: 'clamp(12px, 1.5vw, 20px) clamp(12px, 1.5vw, 20px) 0 0',
          marginBottom: '0',
          marginTop: '0',
          overflow: 'hidden',
          boxShadow: '0 -10px 50px rgba(0,0,0,0.3)',
          opacity: startAnimation ? 1 : 0,
          transition: 'opacity 1.5s ease-out',
          zIndex: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center'
        }}
      >
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'bottom center',
            display: 'block',
            borderRadius: 'clamp(12px, 1.5vw, 20px) clamp(12px, 1.5vw, 20px) 0 0',
            backgroundColor: '#000'
          }}
          muted
          playsInline
          preload='auto'
          fetchPriority='high'
          poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%23000'/%3E%3C/svg%3E"
        >
          <source
            src='https://res.cloudinary.com/dxbjbkvm8/video/upload/v1770042008/bbverse/Banner_top13_removed.mp4'
            type='video/mp4'
          />
        </video>
      </div>
    </section>
  )
}

export default HeroSection
